/**
 * CollapsibleSection Component
 *
 * A reusable collapsible section with smooth Framer Motion animation,
 * ARIA accessibility attributes, and multiple header style variants.
 *
 * Variants:
 * - "colored" — colored background header with border (original style)
 * - "minimal" — borderless, just text + chevron
 * - "inline"  — lightweight text link + chevron (for inline toggles)
 */

import { useId } from "react";
import { Subheading } from "@a7y/tailwind-catalyst";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion } from "framer-motion";
import { DevCardBadge } from "./DevCardBadge";

export interface CollapsibleSectionProps {
  /** Section title text */
  title: string;
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Toggle handler */
  onToggle: () => void;
  /** Header background color (only used with "colored" variant) */
  headerColor?: "green" | "yellow" | "blue" | "gray";
  /** Visual style variant */
  headerVariant?: "colored" | "minimal" | "inline";
  /** Optional ReactNode to render as custom header content (replaces title) */
  headerContent?: React.ReactNode;
  /** When true, renders expanded with no toggle UI (for print mode) */
  forceExpanded?: boolean;
  children: React.ReactNode;
}

const colorClasses = {
  green: "bg-green-100 dark:bg-green-900/30",
  yellow: "bg-yellow-100 dark:bg-yellow-900/30",
  blue: "bg-blue-100 dark:bg-blue-900/30",
  gray: "bg-gray-100 dark:bg-zinc-800",
};

export function CollapsibleSection({
  title,
  isExpanded,
  onToggle,
  headerColor = "gray",
  headerVariant = "colored",
  headerContent,
  forceExpanded = false,
  children,
}: CollapsibleSectionProps) {
  const panelId = useId();

  // Force-expanded mode: render content without toggle UI
  if (forceExpanded) {
    return (
      <DevCardBadge name="CollapsibleSection">
        <div>{children}</div>
      </DevCardBadge>
    );
  }

  const chevron = (
    <ChevronDownIcon
      className={`size-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
        isExpanded ? "rotate-180" : "rotate-0"
      }`}
    />
  );

  const headerLabel = headerContent ?? (
    <Subheading level={4}>{title}</Subheading>
  );

  if (headerVariant === "colored") {
    return (
      <DevCardBadge name="CollapsibleSection">
        <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
          <button
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            className={`w-full ${colorClasses[headerColor]} px-4 py-2 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between hover:opacity-90 transition-opacity text-left`}
          >
            {headerLabel}
            {chevron}
          </button>
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                id={panelId}
                role="region"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="p-4 space-y-4">{children}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DevCardBadge>
    );
  }

  if (headerVariant === "minimal") {
    return (
      <DevCardBadge name="CollapsibleSection">
        <div>
          <button
            onClick={onToggle}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors text-left"
          >
            {chevron}
            <span className="font-medium">{title}</span>
          </button>
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                id={panelId}
                role="region"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="pt-2 space-y-3">{children}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DevCardBadge>
    );
  }

  // inline variant
  return (
    <DevCardBadge name="CollapsibleSection">
      <div>
        <button
          onClick={onToggle}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          className="flex items-center gap-1 text-xs text-brand-green hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors text-left"
        >
          {chevron}
          <span>{title}</span>
        </button>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              id={panelId}
              role="region"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="pt-2 space-y-2">{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DevCardBadge>
  );
}
